import {Attributes, Button, ButtonGroup, DataStatus, DialogFooter} from 'platform/components';
import {Box, Heading, VStack} from 'platform/foundation';

import {useState} from 'react';

import {defaultTo, find, head, isNil, propEq} from 'ramda';

import {useGetHandlingUnitsQuery, usePostCoreExchangeMutation} from '@dms/api/warehouse';
import {useGetOrderItemForMaterialQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {RequiredTestIdProps, composePath, suffixTestId, useNavigate} from 'shared/web';

import type {QueryFilterObject, RowData} from 'features/eag-grid';

import type {CreateCoreExchangeContext} from './types/CreateCoreExchangeContext';

const CORE_ITEM_SELECT_GRID_CODE = 'core-exchange-core-item-select';

interface CreateCoreExchangeDialogProps extends RequiredTestIdProps {
  context: CreateCoreExchangeContext;
  onClose: VoidFunction;
  onCreated?: VoidFunction;
}

/**
 * Creates a core exchange for a service order item. Shows the MAIN item, lets the user pick the
 * CORE item from the other parts of the order and opens the new core exchange on submit.
 */
export function CreateCoreExchangeDialog(props: CreateCoreExchangeDialogProps) {
  const navigate = useNavigate();

  const [selectedCoreItemId, setSelectedCoreItemId] = useState<string | null>(null);

  const {
    data: mainItem,
    isLoading,
    isError,
  } = useGetOrderItemForMaterialQuery({
    serviceCaseId: props.context.serviceCaseId,
    serviceOrderId: props.context.serviceOrderId,
    serviceJobId: props.context.serviceJobId,
    serviceItemId: props.context.serviceOrderJobItemId,
  });

  const {data: handlingUnits, isLoading: areHandlingUnitsLoading} = useGetHandlingUnitsQuery();

  const [postCoreExchange, {isLoading: isCreating}] = usePostCoreExchangeMutation();

  // quantity.unit is the handling unit id, so it is shown by its name
  const unitName = find(propEq(mainItem?.quantity?.unit, 'id'), defaultTo([], handlingUnits))?.name;
  const quantity = isNil(mainItem?.quantity?.amount)
    ? null
    : `${mainItem.quantity.amount} ${unitName ?? ''}`.trim();

  const queryModifier = (filter: QueryFilterObject) => ({
    ...filter,
    serviceOrderId: props.context.serviceOrderId,
    mainItemId: props.context.serviceOrderJobItemId,
  });

  const handleRowSelectionChange = (rows: RowData[]) =>
    setSelectedCoreItemId(head(rows)?.id ?? null);

  const handleSubmit = () => {
    if (isNil(selectedCoreItemId) || isNil(mainItem)) {
      return;
    }

    postCoreExchange({
      originEntityId: props.context.serviceOrderId,
      originEntityType: 'service-case-order',
      items: [
        {itemType: 'MAIN', itemOriginEntityId: props.context.serviceOrderJobItemId},
        {itemType: 'CORE', itemOriginEntityId: selectedCoreItemId},
      ],
    })
      .unwrap()
      .then(({coreExchangeId}) => {
        props.onClose();
        props.onCreated?.();
        navigate(
          composePath(warehouseRoutes.coreExchangeDetailOverview, {
            params: {id: coreExchangeId},
            isSearchParamsPreserved: false,
          })
        );
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      {/* 204: the item was removed or moved meanwhile - show the empty state, not blank fields.
          The core item grid needs a valid MAIN item, so it stays hidden until one is loaded. */}
      <DataStatus
        isLoading={isLoading || areHandlingUnitsLoading}
        isError={isError}
        isEmpty={isNil(mainItem)}
      >
        <VStack spacing={4}>
          <Heading size={4}>{i18n.t('entity.warehouse.labels.coreExchangeMainItem')}</Heading>
          <Attributes
            rows={[
              {label: i18n.t('general.labels.name'), value: mainItem?.name},
              {label: i18n.t('entity.warehouse.labels.catalogueNumber'), value: mainItem?.number},
              {label: i18n.t('general.labels.quantity'), value: quantity},
            ]}
            data-testid={suffixTestId('mainItem', props)}
          />
          <Heading size={4}>{i18n.t('entity.coreExchange.labels.selectCoreItem')}</Heading>
          <Box height="50vh">
            <DmsDataGrid
              gridCode={CORE_ITEM_SELECT_GRID_CODE}
              queryModifier={queryModifier}
              onRowSelectionChange={handleRowSelectionChange}
              emptyState={{headline: i18n.t('entity.coreExchange.labels.noCoreItemsAvailable')}}
              data-testid={suffixTestId('coreItemGrid', props)}
            />
          </Box>
        </VStack>
      </DataStatus>
      <DialogFooter>
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            onClick={props.onClose}
            isDisabled={isCreating}
            data-testid={suffixTestId('close', props)}
            title={i18n.t('general.labels.discard')}
          />
          <Button
            onClick={handleSubmit}
            isDisabled={isNil(selectedCoreItemId) || isNil(mainItem)}
            isLoading={isCreating}
            data-testid={suffixTestId('submit', props)}
            title={i18n.t('entity.coreExchange.actions.addCoreItem')}
          />
        </ButtonGroup>
      </DialogFooter>
    </VStack>
  );
}
